var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/core.ts
import { isError } from "@base/utils/error/guards";
import { parse } from "@base/utils/json";
import { G } from "@mobily/ts-belt";
import { getClientEnv as getCoreClientEnv, getRawEnv } from "./client-env.js";
import { env } from "#validated-env";
function getEnv(key, options = {}) {
  const { defaultValue, required = false, transform } = options;
  const rawValue = env[key] ?? getCoreClientEnv()[key] ?? getRawEnv()[key];
  if (G.isNullable(rawValue) || rawValue === "") {
    if (required) {
      throw new Error(`Missing required environment variable: ${key}`);
    }
    return defaultValue;
  }
  const stringValue = String(rawValue).split(String.raw`\n`).join("\n");
  if (transform) {
    try {
      return transform(stringValue);
    } catch (error) {
      const message = isError(error) ? error.message : "transformation failed";
      throw new Error(`Invalid environment variable ${key}: ${message}`, { cause: error });
    }
  }
  return stringValue;
}
__name(getEnv, "getEnv");
function getEnvString(key, defaultValue = "") {
  return getEnv(key, { defaultValue });
}
__name(getEnvString, "getEnvString");
function getClientEnv(key) {
  return getCoreClientEnv()[key];
}
__name(getClientEnv, "getClientEnv");
function getEnvOrThrow(key, options = {}) {
  return getEnv(key, { required: true, ...options });
}
__name(getEnvOrThrow, "getEnvOrThrow");
function getEnvBoolean(key, defaultValue = false) {
  return getEnv(key, {
    defaultValue,
    transform: /* @__PURE__ */ __name((value) => {
      const lower = value.toLowerCase();
      return lower === "true" || lower === "1" || lower === "yes";
    }, "transform")
  });
}
__name(getEnvBoolean, "getEnvBoolean");
function getEnvNumber(key, defaultValue = 0) {
  return getEnv(key, {
    defaultValue,
    transform: /* @__PURE__ */ __name((value) => {
      const parsed = Number(value);
      if (Number.isNaN(parsed)) {
        throw new Error(`Expected number, got: ${value}`);
      }
      return parsed;
    }, "transform")
  });
}
__name(getEnvNumber, "getEnvNumber");
function getEnvUrl(key, defaultValue = "") {
  return getEnv(key, {
    defaultValue,
    transform: /* @__PURE__ */ __name((value) => {
      try {
        new URL(value);
        return value;
      } catch {
        if (value.includes("{{") || value.includes("}}")) {
          return value;
        }
        throw new Error(`Expected valid URL, got: ${value}`);
      }
    }, "transform")
  });
}
__name(getEnvUrl, "getEnvUrl");
function getEnvArray(key, defaultValue = []) {
  return getEnv(key, {
    defaultValue,
    transform: /* @__PURE__ */ __name((value) => value.split(",").map((s) => s.trim()).
    filter(Boolean), "transform")
  });
}
__name(getEnvArray, "getEnvArray");
function getEnvJson(key, defaultValue = {}) {
  return getEnv(key, {
    defaultValue,
    transform: /* @__PURE__ */ __name((value) => {
      try {
        return parse(value);
      } catch {
        throw new Error(`Expected valid JSON, got: ${value}`);
      }
    }, "transform")
  });
}
__name(getEnvJson, "getEnvJson");
function getEnvironment() {
  const envValue = getEnv("NEXT_PUBLIC_ENVIRONMENT") || getRawEnv()["NODE_ENV"] ||
  "production";
  if (envValue === "development") {
    return "development";
  }
  if (envValue === "test") {
    return "test";
  }
  if (envValue === "staging") {
    return "staging";
  }
  return "production";
}
__name(getEnvironment, "getEnvironment");
function isE2EBypassPermitted() {
  const environment = getEnvironment();
  return environment === "test" || environment === "development";
}
__name(isE2EBypassPermitted, "isE2EBypassPermitted");
function isProduction() {
  return getEnvironment() === "production";
}
__name(isProduction, "isProduction");
function isStaging() {
  return getEnvironment() === "staging";
}
__name(isStaging, "isStaging");
function isDevelopment() {
  return getEnvironment() === "development";
}
__name(isDevelopment, "isDevelopment");
function isTest() {
  return getEnvironment() === "test";
}
__name(isTest, "isTest");
function shouldUseSecureCookies() {
  return isProduction();
}
__name(shouldUseSecureCookies, "shouldUseSecureCookies");
function parseEnvString(envString) {
  return envString.split("\n").reduce(
    (acc, line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith("#")) {
        return acc;
      }
      const equalIndex = line.indexOf("=");
      if (equalIndex === -1) {
        return acc;
      }
      const key = line.substring(0, equalIndex).trim();
      const value = line.substring(equalIndex + 1).trim();
      if (key && value) {
        const cleanValue = value.replace(/^["']|["']$/g, "");
        acc[key] = cleanValue;
      }
      return acc;
    },
    {}
  );
}
__name(parseEnvString, "parseEnvString");
function hasEnv(key) {
  const value = env[key] ?? getRawEnv()[key];
  return G.isNotNullable(value) && value !== "";
}
__name(hasEnv, "hasEnv");
function getEnvs(config) {
  const result = {};
  for (const [field, options] of Object.entries(config)) {
    result[field] = getEnv(options.key, options);
  }
  return result;
}
__name(getEnvs, "getEnvs");
export {
  getClientEnv,
  getEnv,
  getEnvArray,
  getEnvBoolean,
  getEnvJson,
  getEnvNumber,
  getEnvOrThrow,
  getEnvString,
  getEnvUrl,
  getEnvironment,
  getEnvs,
  hasEnv,
  isDevelopment,
  isE2EBypassPermitted,
  isProduction,
  isStaging,
  isTest,
  parseEnvString,
  shouldUseSecureCookies
};
